import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { matchesDropdownSearch } from "./dropdownSearchMatch";
import { createPortal } from "react-dom";

export type SearchableSelectOption = {
  value: string;
  label: string;
  color?: string;
  disabled?: boolean;
  /** Hover text, for example why the option is disabled. */
  title?: string;
  /** Short grey text at the end of the option row (design option-meta). */
  meta?: string;
};

// Long labels end in an ellipsis on one line (design SearchableSelect.jsx),
// so the trigger keeps its 42px row height in narrow grids.
const optionLabel = (option: SearchableSelectOption | undefined, fallback: string, colorSize: 14 | 18 = 14) =>
  option?.color ? (
    <span className="flex min-w-0 items-center gap-[8px]">
      <span aria-hidden="true" className="shrink-0 rounded-full" style={{ backgroundColor: option.color, width: colorSize, height: colorSize }} />
      <span className="min-w-0 truncate">{option.label}</span>
    </span>
  ) : option?.label || fallback;
type SearchableSelectBaseProps = {
  id: string;
  options: readonly SearchableSelectOption[];
  label?: React.ReactNode;
  placeholder?: string;
  searchPlaceholder?: string;
  helperText?: React.ReactNode;
  description?: React.ReactNode;
  error?: string;
  /** Marks the field invalid (red border, aria-invalid) when the page shows its own error text. */
  invalid?: boolean;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  maxSelected?: number;
  selectionDisplay?: "chips" | "summary";
  leadingIcon?: React.ReactNode;
  className?: string;
};
export type SearchableSelectFieldProps = SearchableSelectBaseProps &
  (
    | { multiple?: false; value: string; onChange: (value: string) => void }
    | { multiple: true; value: string[]; onChange: (value: string[]) => void }
  );

export const SearchableSelectField = (props: SearchableSelectFieldProps) => {
  const {
    id,
    options,
    label,
    placeholder = "Select",
    searchPlaceholder = "Search...",
    helperText,
    description,
    error,
    invalid = false,
    required,
    disabled,
    loading,
    maxSelected,
    selectionDisplay = "chips",
    leadingIcon,
    className = "",
  } = props;
  const triggerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [position, setPosition] = useState({
    left: 0,
    top: 0,
    width: 0,
    maxHeight: 355,
  });
  const selectedValues = props.multiple
    ? props.value
    : props.value
      ? [props.value]
      : [];
  const labelFor = (value: string) =>
    options.find((option) => option.value === value)?.label || value;
  // Full text on hover when the one-line trigger shows an ellipsis.
  const triggerTitle = props.multiple
    ? selectionDisplay === "chips" && selectedValues.length
      ? ""
      : selectedValues.length
        ? selectedValues.map(labelFor).join(", ")
        : placeholder
    : props.value
      ? labelFor(props.value)
      : placeholder;
  const filtered = useMemo(
    () =>
      options.filter((option) =>
        matchesDropdownSearch(option.label, search),
      ),
    [options, search],
  );
  const capped =
    props.multiple &&
    maxSelected !== undefined &&
    props.value.length >= maxSelected;
  const isDisabled = (option: SearchableSelectOption) =>
    option.disabled || (capped && !selectedValues.includes(option.value));

  const positionPanel = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const height = 68 + Math.min(options.length || 1, 7) * 41;
    const below = window.innerHeight - rect.bottom - 12;
    const above = rect.top - 12;
    const useAbove = below < Math.min(height, 220) && above > below;
    const maxHeight = Math.min(height, useAbove ? above : below);
    setPosition({
      left: Math.max(
        8,
        Math.min(rect.left, window.innerWidth - rect.width - 8),
      ),
      width: Math.min(rect.width, window.innerWidth - 16),
      top: useAbove ? rect.top - maxHeight - 4 : rect.bottom + 4,
      maxHeight,
    });
  }, [options.length]);

  useEffect(() => {
    if (!open) return;
    positionPanel();
    searchRef.current?.focus();
    const outside = (event: MouseEvent) => {
      if (
        !triggerRef.current?.contains(event.target as Node) &&
        !panelRef.current?.contains(event.target as Node)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    window.addEventListener("resize", positionPanel);
    window.addEventListener("scroll", positionPanel, true);
    return () => {
      document.removeEventListener("mousedown", outside);
      window.removeEventListener("resize", positionPanel);
      window.removeEventListener("scroll", positionPanel, true);
    };
  }, [open, positionPanel]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };
  const select = (option: SearchableSelectOption) => {
    if (isDisabled(option)) return;
    if (props.multiple) {
      props.onChange(
        props.value.includes(option.value)
          ? props.value.filter((value) => value !== option.value)
          : [...props.value, option.value],
      );
      searchRef.current?.focus();
    } else {
      props.onChange(option.value);
      close();
    }
  };
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      let next = activeIndex;
      for (let count = 0; count < filtered.length; count += 1) {
        next = (next + direction + filtered.length) % filtered.length;
        if (!isDisabled(filtered[next])) break;
      }
      setActiveIndex(next);
      panelRef.current
        ?.querySelector(`[data-option-index="${next}"]`)
        ?.scrollIntoView({ block: "nearest" });
    }
    if (event.key === "Enter" && filtered[activeIndex]) {
      event.preventDefault();
      select(filtered[activeIndex]);
    }
    if (event.key === "Tab") setOpen(false);
  };
  const openPanel = () => {
    setSearch("");
    setActiveIndex(0);
    positionPanel();
    setOpen(true);
  };

  return (
    <div className={`min-w-0 ${className}`}>
      {label && (
        <label
          htmlFor={id}
          className="mb-[6px] block text-[14px] font-bold leading-[20px] text-[#0A0A0A]"
        >
          {label}
          {required && <span className="text-[#F0202E]">*</span>}
        </label>
      )}
      {description && (
        <div className="mb-[6px] text-[12px] leading-[1.5] text-[#727272]">
          {description}
        </div>
      )}
      <div
        ref={triggerRef}
        data-member-select-trigger
        data-member-field-error={Boolean(error) || invalid}
        className="relative flex w-full items-center gap-[8px] rounded-[8px] border px-[12px] py-[10px] text-[14px] leading-[20px]"
        style={{
          borderColor: error || invalid ? "#E24C4C" : disabled ? "#ECECEC" : "#9E9E9E",
          background: disabled ? "#F7F7F8" : "#FFFFFF",
        }}
      >
        {leadingIcon && (
          <span
            aria-hidden="true"
            className="flex shrink-0 items-center text-[#727272]"
          >
            {leadingIcon}
          </span>
        )}
        {props.multiple && selectionDisplay === "chips" && props.value.length > 0 && (
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-[6px]">
            {props.value.map((value) => (
              <span
                key={value}
                className="inline-flex min-w-0 items-center gap-[6px] rounded-full border border-[#9E9E9E] bg-white py-[3px] pl-[10px] pr-[6px] text-[13px] font-semibold leading-[20px] text-[#0A0A0A]"
              >
                <span className="truncate">
                  {optionLabel(options.find((option) => option.value === value), value)}
                </span>
                <button
                  type="button"
                  disabled={disabled}
                  title={`Remove ${options.find((option) => option.value === value)?.label || value}`}
                  aria-label={`Remove ${options.find((option) => option.value === value)?.label || value}`}
                  className="relative z-[1] flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full bg-[#F21B1B] text-white disabled:opacity-50"
                  onClick={() =>
                    props.onChange(props.value.filter((item) => item !== value))
                  }
                >
                  <svg
                    width="8"
                    height="8"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    aria-hidden="true"
                  >
                    <path d="m2 2 8 8M10 2l-8 8" />
                  </svg>
                </button>
              </span>
            ))}
          </span>
        )}
        <button
          ref={buttonRef}
          id={id}
          type="button"
          disabled={disabled}
          aria-label={!label && props.multiple ? placeholder : undefined}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? `${id}-options` : undefined}
          aria-describedby={error || helperText ? `${id}-helper` : undefined}
          aria-invalid={Boolean(error) || invalid || undefined}
          onClick={() => (open ? close() : openPanel())}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              openPanel();
            }
          }}
          className={`static flex items-center justify-between bg-transparent text-left font-normal outline-none after:absolute after:inset-0 after:rounded-[8px] after:content-[''] focus-visible:after:ring-2 focus-visible:after:ring-cpaGold disabled:cursor-not-allowed ${props.multiple && selectionDisplay === "chips" && selectedValues.length ? "w-[16px] shrink-0" : "min-w-[28px] flex-1 gap-[8px]"}`}
        >
          <span
            title={triggerTitle || undefined}
            className={`min-w-0 flex-1 truncate ${
              selectedValues.length ? "text-[#0A0A0A]" : "text-[#727272]"
            }`}
          >
            {props.multiple
              ? selectedValues.length
                ? selectionDisplay === "summary"
                  ? `${options.find((option) => option.value === selectedValues[0])?.label || selectedValues[0]}${selectedValues.length > 1 ? ` +${selectedValues.length - 1}` : ""}`
                  : ""
                : placeholder
              : optionLabel(options.find((option) => option.value === props.value), placeholder, 18)}
          </span>
          <svg
            width="16"
            height="16"
            className={`ml-auto shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
          >
            <path d="m4 6 4 4 4-4" />
          </svg>
        </button>
      </div>
      {(error || helperText) && (
        <div
          id={`${id}-helper`}
          role={error ? "alert" : undefined}
          className={`mt-[4px] text-[14px] leading-[20px] ${error ? "text-[#E24C4C]" : "text-[#727272]"}`}
        >
          {error || helperText}
        </div>
      )}
      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-[11000] flex flex-col overflow-hidden rounded-[8px] border border-[#9E9E9E] bg-white text-[#0A0A0A]"
            style={{
              ...position,
              boxShadow: "0 8px 20px rgba(0,0,0,.10)",
              fontFamily: '"DM Sans", sans-serif',
            }}
            onKeyDown={onKeyDown}
          >
            <div className="shrink-0 p-[8px]">
              <input
                ref={searchRef}
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setActiveIndex(0);
                }}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded="true"
                aria-controls={`${id}-options`}
                aria-activedescendant={
                  filtered[activeIndex]
                    ? `${id}-option-${activeIndex}`
                    : undefined
                }
                className="w-full rounded-[8px] border border-[#9E9E9E] bg-white px-[12px] py-[8px] text-[14px] outline-none focus:border-cpaGold"
              />
            </div>
            <div
              id={`${id}-options`}
              role="listbox"
              aria-label={typeof label === "string" ? label : placeholder}
              aria-multiselectable={props.multiple || undefined}
              className="min-h-0 overflow-y-auto"
            >
              {loading ? (
                <p
                  role="status"
                  className="px-[12px] py-[10px] text-[14px] text-[#727272]"
                >
                  Loading...
                </p>
              ) : filtered.length === 0 ? (
                <p className="px-[12px] py-[10px] text-[14px] text-[#727272]">
                  No matches found
                </p>
              ) : (
                filtered.map((option, index) => {
                  const checked = selectedValues.includes(option.value);
                  const optionDisabled = isDisabled(option);
                  return (
                    <div
                      key={option.value}
                      id={`${id}-option-${index}`}
                      data-option-index={index}
                      role="option"
                      aria-selected={checked}
                      aria-disabled={optionDisabled || undefined}
                      title={
                        optionDisabled && capped && !option.disabled
                          ? `Maximum of ${maxSelected} selections — unselect one first`
                          : option.title
                      }
                      onMouseDown={(event) => event.preventDefault()}
                      onMouseMove={() => setActiveIndex(index)}
                      onClick={() => select(option)}
                      className={`flex items-center gap-[10px] px-[12px] py-[10px] text-[14px] leading-[20px] ${checked ? "font-semibold" : "font-normal"} ${optionDisabled ? "cursor-not-allowed text-[#C9C9C9] opacity-60" : "cursor-pointer hover:bg-[#F7F7F8]"} ${index === activeIndex ? "bg-[#F7F7F8]" : ""}`}
                    >
                      {props.multiple && (
                        <span
                          aria-hidden="true"
                          className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border-[1.5px]"
                          style={{
                            borderColor: checked ? "#10B981" : "#9E9E9E",
                            color: "#10B981",
                          }}
                        >
                          {checked && (
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="m2 6 3 3 5-6" />
                            </svg>
                          )}
                        </span>
                      )}
                      {optionLabel(option, option.label)}
                      {option.meta ? <span className="ml-auto shrink-0 text-[12px] text-[#727272]">{option.meta}</span> : null}
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default SearchableSelectField;
